import { useTranslation } from 'react-i18next';
import {
  Alert,
  Snackbar,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { unwrapResult } from '@reduxjs/toolkit';
import CallToActionButton from '../common/CallToActionButton';
import {
  selectGetOnboardingFormStatus,
  selectMemberSession, updateOnboardingLandingPageViewDone,
} from '../store/slices/onboardingSlice';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { JoistSolutionOrgName } from '../common/constants';
import { TermsOfServiceWrapper } from './TermsOfServiceWrapper';
import { TermsOfServiceWrapperJoist } from './TermsOfServiceWrapperJoist';
import { useSolutionOrg } from '../config/useSolutionOrg';
import { enrollMember, getOnboardingForm } from '../store/thunks/onboardingThunk';
import { EnrollmentRequest } from '../common/models/EnrollmentRequest';
import { HOME_DEPOT_SETUP_OR_SKIP_ROUTE } from '../routes/namedRoutes';
import { TestIds } from '../assets/testIds';
import { SplashScreenSecondVariant } from './SplashScreens/SplashScreenSecondVariant';
import { useOptimizelyFlag } from '../common/hooks/useOptimizelyFlag';
import {
  splashScreenCtaText,
  SplashScreenFlag,
  splashScreenHeading,
  splashScreenImageUrl,
  splashScreenSubHeading,
} from '../common/OptimizelyFlagsConstants';
import { Loader } from '../common/Loader';
import { useCustomEventTracking } from '../common/useCustomEventTracking';
import { EnrollmentResponse } from '../common/models/EnrollmentResponse';

export function Home() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();
  const {
    memberId: storeMemberId,
  } = useAppSelector(selectMemberSession);
  const [searchParams] = useSearchParams();
  const memberId = searchParams.get('memberId') || storeMemberId;
  const {
    solutionOrgName, envSolutionOrgFormId,
    envSolutionOrgId,
    enrollOnSplashScreen,
  } = useSolutionOrg();
  const onboardFormId = envSolutionOrgFormId as string;
  const getOnboardingFormStatus = useAppSelector(selectGetOnboardingFormStatus);
  const [loading, setLoading] = useState(false);
  const [openErrorSnackbar, setOpenErrorSnackbar] = useState(false);
  const { trackEdgeEnrollment, trackRequest } = useCustomEventTracking();

  // check company enrollment status

  useEffect(() => {
    // only fetch form if we are not completing enrollment on the splash screen
    if (!enrollOnSplashScreen) {
      dispatch(getOnboardingForm(
        {
          formId: onboardFormId,
          trackRequest,
        },
      ));
    }
    // generate a rewards splash screen loaded event
    trackEdgeEnrollment('View', 'SplashScreen');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navigateToOnboard = () => {
    trackEdgeEnrollment('Click', 'SplashScreenBtn', { isOptimizelyEvent: true });

    // onboarding landing page screen view done
    dispatch(updateOnboardingLandingPageViewDone(true));

    navigate('form');
  };

  const enrollToEdge = () => {
    // Enroll with home depot as partner without completing company and business goals forms.
    setLoading(true);
    trackEdgeEnrollment('Click', 'SplashScreenBtn');
    const request: EnrollmentRequest = {
      memberId,
      member: {},
      forms: [],
      partners: [], // Do not auto-enroll partners during edge enrollment
    };

    const solutionOrgId = (envSolutionOrgId as string) || '';
    dispatch(enrollMember({ solutionOrgId, request, trackRequest }))
      .then(unwrapResult)
      .then((enrollmentResponse) => {
        setOpenErrorSnackbar(false);

        if ((enrollmentResponse as EnrollmentResponse).isFirstEdgeEnrollmentInCompany) {
          // send a rewards everpro signup completed event
          trackEdgeEnrollment('Submit', 'Signup', { isOptimizelyEvent: true });
        }

        // navigate to home depot setup screen
        navigate(HOME_DEPOT_SETUP_OR_SKIP_ROUTE);
      })
      .catch((error: unknown) => {
        setOpenErrorSnackbar(true);
        setLoading(false);
      });
  };

  const handleErrorClose = () => {
    setOpenErrorSnackbar(false);
  };

  const HomeFooter = ({ setupBtnText }: { setupBtnText: string }) => (
    <>
      {solutionOrgName === JoistSolutionOrgName ?
        <TermsOfServiceWrapperJoist setupBtnText={setupBtnText} /> :
        <TermsOfServiceWrapper setupBtnText={setupBtnText} />}
    </>
  );

  const LetsGoBtn = ({ btnText }: { btnText: string }) => (
    <CallToActionButton
      text={btnText}
      data-testid={TestIds.onboarding.enrollBtn}
      disable={loading}
      onBtnClick={enrollOnSplashScreen ? enrollToEdge : navigateToOnboard}
      sx={{
        background: theme.palette.brand.primary,
        '&:hover': {
          backgroundColor: theme.palette.brand.primary,
        },
        '&:disabled': {
          backgroundColor: theme.palette.brand.primary,
          opacity: '0.4',
          color: theme.palette.common.white,
        },
        position: 'static',
        width: '100%',
        borderRadius: theme.spacing(8),
        padding: theme.spacing(1.875),
        [theme.breakpoints.up('sm')]: {
          width: '100%',
          marginLeft: '0px',
        },
        [theme.breakpoints.up('lg')]: {
          position: 'static',
          marginLeft: 'auto',
          marginRight: 'auto',
          // Change on button to make sure everything is in one screen
          marginTop: theme.spacing(2.5),
          marginBottom: theme.spacing(2.5),
          width: '100%',
          maxWidth: theme.spacing(75),
        },
      }}
    />);

  // get component based on optimizely decision
  const { decision, isDecisionReady, loaderComponent } = useOptimizelyFlag({
    flag: SplashScreenFlag,
  });

  return (
    <>
      <Snackbar
        open={openErrorSnackbar}
        autoHideDuration={4000}
        onClose={handleErrorClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Alert severity="error" sx={{ width: '100%' }}>
          {t('error.generalMessage')}
        </Alert>
      </Snackbar>
      {getOnboardingFormStatus === 'pending' && !enrollOnSplashScreen && <Loader />}
      {!isDecisionReady ? loaderComponent :
        <SplashScreenSecondVariant // making second variant as default and fallback
          Footer={HomeFooter}
          CallToActionBtn={LetsGoBtn}
          heading={decision?.variables?.[splashScreenHeading] as string}
          subHeading={decision?.variables?.[splashScreenSubHeading] as string}
          imageUrl={decision?.variables?.[splashScreenImageUrl] as string}
          ctaText={decision?.variables?.[splashScreenCtaText] as string}
        />
      }
    </>
  );
}
